import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { illustrativeExplanation, illustrativeLabel, type Solution } from '../../data/solutionCatalog';
import { demoUrlFor } from '../../data/demoCenter';
import { fadeUp } from '../../utils/motion';
import { AssumptionsDisclosure } from './AssumptionsDisclosure';
import { GovernmentScenario } from './GovernmentScenario';
type ImpactPanelProps = {
  solution: Solution;
  headingLevel: 'h2' | 'h3';
};

/** Potential impact: three benefits, one illustrative scenario, its assumptions and a demo link. */
export function ImpactPanel({
  solution,
  headingLevel
}: ImpactPanelProps) {
  const {
    impact
  } = solution;
  const Heading = headingLevel;
  const titleId = `${solution.id}-impact-title`;
  return <section aria-labelledby={titleId} className="bg-surface-bg border-b border-line py-16 lg:py-24" data-id="element-2476" data-mp="3cgff2c1ahrub">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2477" data-mp="3hf0ewacmq9i8">
        <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 mb-10" data-id="element-2478" data-mp="3heh73mgp11hx">
          <div className="lg:col-span-5" data-id="element-2479" data-mp="3i6p05pypvkik">
            <p className="text-sm font-medium text-teal mb-3" data-id="element-2480" data-mp="3da7196q2ige5">Potential impact</p>
            <Heading id={titleId} className="font-heading font-bold text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-2481" data-mp="3ryl8ruyfit11">
              {impact.title}
            </Heading>
          </div>
          {impact.supporting && <p className="lg:col-span-7 lg:pt-9 text-lg text-ink-dark leading-relaxed" data-id="element-2482" data-mp="2z94bojzv6nyh">{impact.supporting}</p>}
        </motion.div>

        <motion.ul {...fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line rounded-lg overflow-hidden" data-id="element-2483" data-mp="2ucnmdmgxi3jt">
          {impact.benefits.map(benefit => <li key={benefit.title} className="bg-white p-6 lg:p-7" data-id="element-2484" data-mp="34bd7rp81a64t">
              <p className="font-heading font-bold text-lg text-ink-dark leading-snug" data-id="element-2485" data-mp="nis731v3zurk">{benefit.title}</p>
              <p className="mt-2 text-ink-muted leading-relaxed" data-id="element-2486" data-mp="jii3fvushgfv">{benefit.body}</p>
            </li>)}
        </motion.ul>

        <motion.div {...fadeUp} className="mt-8 rounded-lg border border-line bg-white p-6 lg:p-8" data-id="element-2487" data-mp="3hccsdd32w08l">
          <p className="text-sm font-semibold text-ink-dark" data-id="element-2488" data-mp="3mbfwmvb8i2fi">{illustrativeLabel}</p>
          <p className="mt-1 text-sm text-ink-muted leading-relaxed max-w-4xl" data-id="element-2489" data-mp="3m9bnnyyhad8d">{illustrativeExplanation}</p>

          {impact.scenario.kind === 'government' ? <GovernmentScenario data-id="element-2490" data-mp="2to1vel0i6kpl" /> : <>
              <p className="mt-5 font-heading font-semibold text-xl lg:text-2xl text-ink-dark leading-snug max-w-4xl" data-id="element-2491" data-mp="2ylbu6r9s88q3">
                {impact.scenario.text}
              </p>
              <AssumptionsDisclosure data-id="element-2492" data-mp="32lm2ial1rb84">
                <ul className="space-y-2" data-id="element-2493" data-mp="37ef810ocanl9">
                  {impact.scenario.assumptions.map(assumption => <li key={assumption} className="text-sm text-ink-dark leading-relaxed" data-id="element-2494" data-mp="2avhkgikrf0a2">
                      {assumption}
                    </li>)}
                </ul>
              </AssumptionsDisclosure>
              <p className="mt-5 text-sm text-ink-muted leading-relaxed" data-id="element-2495" data-mp="27c8xaw5lgh2m">
                <span className="font-semibold text-ink-dark" data-id="element-2496" data-mp="22frztbci8qcv">How we would measure it in a pilot: </span>
                {impact.scenario.measures}
              </p>
            </>}

          <a href={demoUrlFor(solution.id)} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-1.5 font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2497" data-mp="1tdsg2ba3v868">
            {solution.demoCta}
            <ArrowUpRight size={16} aria-hidden="true" data-id="element-2498" data-mp="1epxncuw3l3t5" />
            <span className="sr-only" data-id="element-2499" data-mp="1aovncbucwnq4">(opens in a new tab)</span>
          </a>
        </motion.div>
      </div>
    </section>;
}